import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle, FileText, History, Printer } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  attach277caClaimStatus,
  getBillingClaimWorksheet,
  getBillingCycleClaimTimeline,
  listUnmatched277caClaimStatuses,
  resolveClaimTransmission,
  stediGetClaimUb04Pdf,
} from '../../services/api';
import type {
  ActiveBillingCycleItem,
  BillingCycleWorksheetRead,
  ClaimStatusEvidenceResponse,
  ClaimTimelineResponse,
  ClaimTransmissionOutcome,
  ClaimTransmissionResponse,
  ClaimVersionTimelineEntry,
  RemittanceClaimDisposition,
  Unmatched277CaEvidenceResponse,
} from '../../services/api/types.gen';
import {
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
} from '../../utils/apiErrorDetail';
import { formatDateTimeInAgencyZone } from '../../utils/dateUtils';
import { PermissionSlug } from '../../constants/featureFlags';
import { usePdfTab } from '../../hooks/usePdfTab';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import { formatRemittanceCurrency } from './remittance/remittanceFormatters';
import ClaimWorksheetEditor from './ClaimWorksheetEditor';
import { terminalWorksheetViewStatuses } from './worksheet/worksheetUtils';

/**
 * The claim-version timeline for one billing cycle (EMR-1409).
 *
 * Every claim version in the chain — the original plus each replacement — in
 * sequence order, each with its outbound transmissions and the 277CA / 835
 * evidence linked to THAT version, so a biller can tell the denied original
 * from each correction before acting on it. Versions link to their
 * predecessor and their replacement, so the chain can be walked both ways.
 */

const KIND_LABELS: Record<string, string> = {
  original: 'Original',
  replacement: 'Replacement',
  resubmission: 'Resubmission',
};

const TRANSMISSION_OUTCOME_LABELS: Record<ClaimTransmissionOutcome, string> = {
  pending: 'Pending',
  success: 'Success',
  uncertain: 'Uncertain',
  failed: 'Failed',
};

const TRANSMISSION_OUTCOME_VARIANTS: Record<ClaimTransmissionOutcome, string> =
  {
    pending: 'text-gray-700 border-gray-300 bg-gray-50',
    success: 'text-green-700 border-green-300 bg-green-50',
    uncertain: 'text-amber-700 border-amber-300 bg-amber-50',
    failed: 'text-red-700 border-red-300 bg-red-50',
  };

/** The error code Undo Mark as Sent leaves on the manual send it reverses. */
const MANUAL_SEND_UNDONE = 'manual_send_undone';

const DISPOSITION_LABELS: Record<RemittanceClaimDisposition, string> = {
  paid: 'Paid',
  denied: 'Denied',
  reversed: 'Reversed',
  other: 'Other',
};

const DISPOSITION_VARIANTS: Record<RemittanceClaimDisposition, string> = {
  paid: 'text-green-700 border-green-300 bg-green-50',
  denied: 'text-red-700 border-red-300 bg-red-50',
  reversed: 'text-orange-700 border-orange-300 bg-orange-50',
  other: 'text-gray-700 border-gray-300 bg-gray-50',
};

/** 'approved_for_submission' -> 'Approved for submission';
 *  'CLEARINGHOUSE' -> 'Clearinghouse'. */
const formatClaimStatus = (status: string): string => {
  const spaced = status.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

function TimelineField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='text-sm font-medium'>{value ?? '—'}</div>
    </div>
  );
}

function TransmissionsTable({
  transmissions,
  canReconcile,
  resolvingTransmissionId,
  onResolve,
}: {
  transmissions: ClaimTransmissionResponse[];
  canReconcile: boolean;
  resolvingTransmissionId: string | null;
  onResolve: (
    transmissionId: string,
    outcome: 'success' | 'failed'
  ) => Promise<void>;
}) {
  if (transmissions.length === 0) return null;
  return (
    <div>
      <h4 className='mb-1 text-xs font-semibold text-muted-foreground'>
        Transmissions
      </h4>
      {/* min-w keeps the columns readable at phone widths: the table scrolls
          inside its container instead of collapsing into tall wrapped rows. */}
      <div className='overflow-x-auto'>
        <Table className='min-w-[520px]'>
          <TableHeader>
            <TableRow>
              <TableHead>Attempt</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Error</TableHead>
              {canReconcile ? <TableHead>Reconcile</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {transmissions.map(tx => (
              <TableRow key={tx.id}>
                <TableCell className='text-sm'>
                  #{tx.attempt_sequence}
                </TableCell>
                <TableCell className='text-sm'>
                  <Badge
                    variant='outline'
                    className={`whitespace-nowrap text-xs ${
                      tx.error_code === MANUAL_SEND_UNDONE
                        ? TRANSMISSION_OUTCOME_VARIANTS.pending
                        : TRANSMISSION_OUTCOME_VARIANTS[tx.outcome]
                    }`}
                  >
                    {tx.error_code === MANUAL_SEND_UNDONE
                      ? 'Undone'
                      : TRANSMISSION_OUTCOME_LABELS[tx.outcome]}
                  </Badge>
                </TableCell>
                <TableCell className='text-sm whitespace-nowrap'>
                  {tx.submission_method
                    ? formatClaimStatus(tx.submission_method)
                    : '—'}
                </TableCell>
                <TableCell className='text-sm whitespace-nowrap'>
                  {formatDateTimeInAgencyZone(tx.started_at) || '—'}
                </TableCell>
                <TableCell className='text-sm'>
                  {tx.error_code === MANUAL_SEND_UNDONE
                    ? 'Mark as sent undone'
                    : tx.error_code
                      ? `${tx.error_code}`
                      : '—'}
                </TableCell>
                {canReconcile ? (
                  <TableCell className='text-sm'>
                    {tx.is_reconciliation_eligible ? (
                      <div className='flex min-w-[240px] flex-wrap gap-1'>
                        <Button
                          type='button'
                          size='sm'
                          disabled={resolvingTransmissionId === tx.id}
                          onClick={() => void onResolve(tx.id, 'success')}
                        >
                          Confirm submitted
                        </Button>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          disabled={resolvingTransmissionId === tx.id}
                          onClick={() => void onResolve(tx.id, 'failed')}
                        >
                          Confirm not submitted
                        </Button>
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatusEvidenceList({
  evidence,
}: {
  evidence: ClaimStatusEvidenceResponse[];
}) {
  if (evidence.length === 0) return null;
  return (
    <div>
      <h4 className='mb-1 text-xs font-semibold text-muted-foreground'>
        277CA claim status
      </h4>
      <ul className='space-y-1'>
        {evidence.map(ev => (
          <li
            key={ev.id}
            className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'
          >
            <Badge
              variant='outline'
              className={`whitespace-nowrap text-xs ${
                ev.is_accepted
                  ? 'text-green-700 border-green-300 bg-green-50'
                  : ev.is_rejected
                    ? 'text-red-700 border-red-300 bg-red-50'
                    : 'text-gray-700 border-gray-300 bg-gray-50'
              }`}
            >
              {ev.is_accepted
                ? 'Accepted'
                : ev.is_rejected
                  ? 'Rejected'
                  : 'Received'}
            </Badge>
            <span className='font-medium whitespace-nowrap'>
              {ev.status_category_code}
            </span>
            {ev.status_category_description ? (
              <span className='text-muted-foreground'>
                {ev.status_category_description}
              </span>
            ) : null}
            {ev.trading_partner_claim_number ? (
              <span className='text-muted-foreground whitespace-nowrap'>
                {ev.trading_partner_claim_number}
              </span>
            ) : null}
            <span className='text-muted-foreground whitespace-nowrap'>
              {formatDateTimeInAgencyZone(ev.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const NON_PAYER_FACING_STATUSES = new Set([
  'draft',
  'pending_documentation',
  'ready_for_review',
  'approved_for_submission',
  'abandoned',
]);

function Unmatched277CAEvidence({
  versions,
  onAttached,
}: {
  versions: ClaimVersionTimelineEntry[];
  onAttached: () => void | Promise<void>;
}) {
  const canReconcile = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_RECONCILE)
  );
  const [evidence, setEvidence] = useState<Unmatched277CaEvidenceResponse[]>(
    []
  );
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const { error: showError, success: showSuccess } = useToast();
  const candidates = versions.filter(
    version => !NON_PAYER_FACING_STATUSES.has(version.status)
  );

  useEffect(() => {
    if (!canReconcile) return;
    let cancelled = false;
    void (async () => {
      const resp = await listUnmatched277caClaimStatuses();
      if (cancelled) return;
      if (resp.error || !resp.data) {
        console.error('Failed to load unmatched 277CA evidence:', resp.error);
        showError(
          getUserFacingApiMessage(
            resp,
            'Unable to load unmatched 277CA evidence.'
          )
        );
        return;
      }
      setEvidence(resp.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [canReconcile, showError]);

  if (!canReconcile || evidence.length === 0) return null;

  const attach = async (statusId: string) => {
    const claimId = targets[statusId];
    if (!claimId) return;
    setBusyId(statusId);
    try {
      const resp = await attach277caClaimStatus({
        path: { status_id: statusId },
        body: { claim_id: claimId },
      });
      if (resp.error) throw resp;
      setEvidence(current => current.filter(item => item.id !== statusId));
      showSuccess('277CA evidence attached');
      await onAttached();
    } catch (error) {
      console.error('Failed to attach 277CA evidence:', error);
      showError(
        getUserFacingApiMessage(error, 'Unable to attach this 277CA evidence.')
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className='space-y-2 rounded-md border border-amber-200 bg-amber-50/40 p-3'>
      <h3 className='text-sm font-semibold'>Unmatched 277CA evidence</h3>
      <p className='text-xs text-muted-foreground'>
        Review payer identifiers before attaching evidence to a claim version.
      </p>
      <ul className='space-y-2'>
        {evidence.map(item => (
          <li
            key={item.id}
            className='flex flex-col gap-2 rounded-md border bg-background p-2 sm:flex-row sm:items-end'
          >
            <div className='min-w-0 flex-1 text-xs'>
              <div className='font-medium'>
                {item.status_category_code}
                {item.status_code ? ` · ${item.status_code}` : ''}
              </div>
              <div className='break-words text-muted-foreground'>
                <span>{item.claim_transaction_batch_number ?? 'No batch'}</span>
                {' · '}
                <span>
                  {item.trading_partner_claim_number ?? 'No payer DCN'}
                </span>
                {' · '}
                <span>{item.patient_control_number ?? 'No PCN'}</span>
              </div>
            </div>
            <label className='flex min-w-0 flex-col gap-1 text-xs'>
              Target claim
              <select
                aria-label={`Target claim for unmatched 277CA ${item.id}`}
                value={targets[item.id] ?? ''}
                onChange={event =>
                  setTargets(current => ({
                    ...current,
                    [item.id]: event.target.value,
                  }))
                }
                className='h-8 max-w-full rounded-md border bg-background px-2'
              >
                <option value=''>Select a version</option>
                {candidates.map(claim => (
                  <option key={claim.claim_id} value={claim.claim_id}>
                    Version {claim.claim_sequence ?? '—'} · PCN{' '}
                    {claim.patient_control_number ?? '—'}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type='button'
              size='sm'
              aria-label={`Attach 277CA ${item.id}`}
              disabled={busyId === item.id || !targets[item.id]}
              onClick={() => void attach(item.id)}
            >
              Attach
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RemittanceEvidenceList({
  evidence,
}: {
  evidence: ClaimVersionTimelineEntry['remittance_evidence'];
}) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <div>
      <h4 className='mb-1 text-xs font-semibold text-muted-foreground'>
        835 remittance
      </h4>
      <ul className='space-y-1'>
        {evidence.map(ev => (
          <li
            key={ev.id}
            className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'
          >
            <Badge
              variant='outline'
              className={`whitespace-nowrap text-xs ${DISPOSITION_VARIANTS[ev.disposition]}`}
            >
              {DISPOSITION_LABELS[ev.disposition]}
            </Badge>
            <span className='whitespace-nowrap'>
              {formatRemittanceCurrency(ev.claim_payment_amount)}
            </span>
            {ev.payer_claim_control_number ? (
              <span className='text-muted-foreground whitespace-nowrap'>
                {ev.payer_claim_control_number}
              </span>
            ) : null}
            <span className='text-muted-foreground whitespace-nowrap'>
              {formatDateTimeInAgencyZone(ev.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const ClaimTimeline: React.FC<{
  billingCycleId: string;
  /** Opens the cycle's editable worksheet — the target of the unsent-draft
   *  version's worksheet link (submitted versions open their own frozen
   *  UB-04 instead). */
  onOpenEditableWorksheet: () => void;
  /** Opens an abandoned version's preserved worksheet in read-only mode. */
  onOpenAbandonedWorksheet: (
    claimId: string,
    worksheet: BillingCycleWorksheetRead
  ) => void;
}> = ({
  billingCycleId,
  onOpenEditableWorksheet,
  onOpenAbandonedWorksheet,
}) => {
  const [timeline, setTimeline] = useState<ClaimTimelineResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusedClaimId, setFocusedClaimId] = useState<string | null>(null);
  const [resolvingTransmissionId, setResolvingTransmissionId] = useState<
    string | null
  >(null);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});
  const timelineRequestRef = useRef(0);
  const openPdfTab = usePdfTab();
  const { error: showError, success: showSuccess } = useToast();
  const canReconcile = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_RECONCILE)
  );

  const loadTimeline = useCallback(
    async (reset: boolean) => {
      const requestId = ++timelineRequestRef.current;
      if (reset) {
        setIsLoading(true);
        setError(null);
        setTimeline(null);
      }
      const resp = await getBillingCycleClaimTimeline({
        path: { billing_cycle_id: billingCycleId },
      });
      if (requestId !== timelineRequestRef.current) return;
      if (resp.error || !resp.data) {
        setError(
          getUserFacingApiMessage(resp, 'Unable to load the claim timeline.')
        );
      } else {
        setTimeline(resp.data);
      }
      setIsLoading(false);
    },
    [billingCycleId]
  );

  useEffect(() => {
    void loadTimeline(true);
    return () => {
      timelineRequestRef.current += 1;
    };
  }, [loadTimeline]);

  // The backend orders by claim_sequence; sort again so the chain reads
  // oldest-first even if a future payload arrives unsorted.
  const versions = useMemo<ClaimVersionTimelineEntry[]>(() => {
    const list = timeline?.versions ?? [];
    return [...list].sort(
      (a, b) => (a.claim_sequence ?? Infinity) - (b.claim_sequence ?? Infinity)
    );
  }, [timeline]);

  const focusVersion = (claimId: string) => {
    setFocusedClaimId(claimId);
    // block: 'nearest' avoids jumping the dialog's scroll when the target is
    // already on screen. Optional call: jsdom does not implement
    // scrollIntoView, and the highlight must not depend on the scroll.
    cardRefs.current[claimId]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const reconcileTransmission = async (
    transmissionId: string,
    outcome: 'success' | 'failed'
  ) => {
    setResolvingTransmissionId(transmissionId);
    try {
      const resp = await resolveClaimTransmission({
        path: { transmission_id: transmissionId },
        body: { outcome },
      });
      if (resp.error || !resp.data) throw resp;
      showSuccess('Transmission reconciled.');
      await loadTimeline(false);
    } catch (err) {
      console.error('Failed to reconcile claim transmission:', err);
      showError('Failed to reconcile the transmission.');
    } finally {
      setResolvingTransmissionId(null);
    }
  };

  const openWorksheet = async (claimId: string) => {
    // Open the tab synchronously inside the click so the browser treats it as
    // user-initiated; otherwise opening it after the awaited fetch is blocked
    // by popup blockers.
    const tab = openPdfTab();
    if (!tab) return;
    try {
      const resp = await stediGetClaimUb04Pdf({
        path: { claim_id: claimId },
        responseType: 'blob',
      });
      if (resp.error) throw resp;
      if (!resp.data) {
        tab.close();
        return;
      }
      tab.setBlob(
        resp.data instanceof Blob
          ? resp.data
          : new Blob([resp.data as BlobPart], { type: 'application/pdf' })
      );
    } catch (err) {
      console.error('Failed to open the claim worksheet:', err);
      tab.close();
      showError(
        await getUserFacingApiMessageAsync(err, 'Failed to open the worksheet.')
      );
    }
  };

  const openAbandonedWorksheet = async (claimId: string) => {
    try {
      const resp = await getBillingClaimWorksheet({
        path: { claim_id: claimId },
      });
      if (resp.error || !resp.data) throw resp;
      onOpenAbandonedWorksheet(claimId, resp.data);
    } catch (err) {
      console.error('Failed to open the abandoned claim worksheet:', err);
      showError(
        await getUserFacingApiMessageAsync(err, 'Failed to open the worksheet.')
      );
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-2 py-4'>
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-20 w-2/3' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center gap-2 py-8 text-sm text-red-600'>
        <AlertCircle className='h-4 w-4' />
        {error}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        No claim versions yet.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <Unmatched277CAEvidence
        versions={versions}
        onAttached={() => loadTimeline(false)}
      />
      {versions.map(version => {
        const predecessor = versions.find(
          v => v.claim_id === version.predecessor_claim_id
        );
        const successor = versions.find(
          v => v.predecessor_claim_id === version.claim_id
        );
        const kindLabel = version.correction_kind
          ? (KIND_LABELS[version.correction_kind] ?? version.correction_kind)
          : '—';
        return (
          <article
            key={version.claim_id}
            ref={el => {
              cardRefs.current[version.claim_id] = el;
            }}
            data-claim-id={version.claim_id}
            data-highlighted={
              focusedClaimId === version.claim_id ? 'true' : undefined
            }
            className={`space-y-3 rounded-md border p-3 ${
              focusedClaimId === version.claim_id ? 'ring-2 ring-primary' : ''
            }`}
          >
            <div className='flex flex-col gap-2 md:flex-row md:items-start md:justify-between'>
              <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
                <h4 className='whitespace-nowrap text-sm font-semibold'>
                  Version {version.claim_sequence ?? '—'}
                </h4>
                <Badge variant='outline' className='whitespace-nowrap text-xs'>
                  {kindLabel}
                </Badge>
                {version.is_current ? (
                  <Badge className='whitespace-nowrap text-xs'>Current</Badge>
                ) : null}
                {version.version_label ? (
                  <Badge
                    variant='outline'
                    className='whitespace-nowrap text-xs text-amber-700 border-amber-300 bg-amber-50'
                  >
                    {version.version_label}
                  </Badge>
                ) : null}
                <Badge variant='outline' className='whitespace-nowrap text-xs'>
                  {formatClaimStatus(version.status)}
                </Badge>
              </div>
              <div className='flex flex-wrap items-center gap-1'>
                {predecessor ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => focusVersion(predecessor.claim_id)}
                  >
                    View predecessor
                  </Button>
                ) : null}
                {successor ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => focusVersion(successor.claim_id)}
                  >
                    View{' '}
                    {(
                      KIND_LABELS[successor.correction_kind ?? 'replacement'] ??
                      'Replacement'
                    ).toLowerCase()}
                  </Button>
                ) : null}
                {version.status === 'abandoned' ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() =>
                      void openAbandonedWorksheet(version.claim_id)
                    }
                  >
                    <FileText className='h-4 w-4' />
                    Claim worksheet (read-only)
                  </Button>
                ) : version.submitted_date ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    aria-label='Worksheet (UB-04)'
                    onClick={() => void openWorksheet(version.claim_id)}
                  >
                    <Printer className='h-4 w-4' />
                    Worksheet (UB-04)
                  </Button>
                ) : (
                  // The unsent draft has no frozen 837I document, so its
                  // worksheet link goes to the cycle's editable worksheet.
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={onOpenEditableWorksheet}
                  >
                    <FileText className='h-4 w-4' />
                    Claim worksheet
                  </Button>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3'>
              <TimelineField
                label='Patient Control #'
                value={version.patient_control_number}
              />
              <TimelineField
                label='Payer Claim Control #'
                value={version.payer_claim_control_number}
              />
              <TimelineField
                label='Created'
                value={formatDateTimeInAgencyZone(version.created_date) || '—'}
              />
              <TimelineField
                label='Submitted'
                value={
                  version.submitted_date
                    ? formatDateTimeInAgencyZone(version.submitted_date)
                    : '—'
                }
              />
            </div>

            <TransmissionsTable
              transmissions={version.transmissions ?? []}
              canReconcile={canReconcile}
              resolvingTransmissionId={resolvingTransmissionId}
              onResolve={reconcileTransmission}
            />
            <StatusEvidenceList evidence={version.status_evidence ?? []} />
            <RemittanceEvidenceList evidence={version.remittance_evidence} />
          </article>
        );
      })}
    </div>
  );
};

/**
 * Row action that opens the cycle's claim timeline in a dialog. Read-gated
 * like the rest of the Billing Manager's data (no submission authority), so
 * it shows for every user who can see the row.
 *
 * Takes the full billing-cycle row (not just the id): an unsent draft
 * version's worksheet link opens the cycle's editable worksheet through
 * here, with the same read-only policy as the row's worksheet action.
 */
export const ClaimTimelineAction: React.FC<{
  billingCycle: ActiveBillingCycleItem;
  /**
   * Runs after an in-editor approval moves the cycle to ready-to-send, so
   * the owning tab can reload its rows and counts (the cycle leaves the
   * tab it was listed under).
   */
  onApproved?: () => void | Promise<void>;
}> = ({ billingCycle, onApproved }) => {
  const [open, setOpen] = useState(false);
  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const [abandonedWorksheet, setAbandonedWorksheet] = useState<{
    claimId: string;
    worksheet: BillingCycleWorksheetRead;
  } | null>(null);
  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              aria-label='Claim timeline'
              className='text-muted-foreground hover:text-foreground'
              onClick={() => setOpen(true)}
            >
              <History className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Claim timeline
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-h-[85vh] max-w-3xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Claim timeline</DialogTitle>
          </DialogHeader>
          <ClaimTimeline
            billingCycleId={billingCycle.billing_cycle_id}
            onOpenEditableWorksheet={() => {
              // One dialog at a time: the timeline closes so the worksheet
              // opens on its own, exactly like the row's worksheet action.
              setOpen(false);
              setAbandonedWorksheet(null);
              setWorksheetOpen(true);
            }}
            onOpenAbandonedWorksheet={(claimId, worksheet) => {
              // Keep the preserved claim-version document with the viewer;
              // the cycle worksheet may now resolve to a later replacement.
              setOpen(false);
              setAbandonedWorksheet({ claimId, worksheet });
              setWorksheetOpen(true);
            }}
          />
        </DialogContent>
      </Dialog>
      {worksheetOpen && (
        <ClaimWorksheetEditor
          key={abandonedWorksheet?.claimId ?? 'editable'}
          billingCycle={billingCycle}
          readOnly={
            abandonedWorksheet !== null ||
            terminalWorksheetViewStatuses.has(billingCycle.billing_cycle_status)
          }
          initialWorksheet={abandonedWorksheet?.worksheet}
          onClose={() => {
            setWorksheetOpen(false);
            setAbandonedWorksheet(null);
          }}
          onApproved={onApproved}
        />
      )}
    </>
  );
};

export default ClaimTimeline;
